import React, { useState, useEffect } from 'react';
import './EmployeeManagement.css';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import EmployeeImageUpload from '../EmployeeImageUpload';

// Enhanced Employee interface with comprehensive HR fields
interface Employee {
  employeeId: string;
  employeeNumber: string;
  enterpriseId: string;
  storeId: string; // Home store (primary store assignment)
  additionalStoreIds?: string[]; // Additional stores where employee can work
  
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  socialSecurityNumber: string; // Should be encrypted in production
  
  // Address Information  
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  
  // Emergency Contact
  emergencyContact: {
    name: string;
    relationship: string;
    phone: string;
    email: string;
  };
  
  // Employment Information
  jobId: string;
  jobTitle: string;
  department: string;
  hireDate: string;
  terminationDate?: string;
  employmentType: 'full-time' | 'part-time' | 'contractor';
  status: 'active' | 'inactive' | 'terminated' | 'on_leave';
  
  // Compensation
  hourlyRate: number;
  salaryAmount: number;
  payType: 'hourly' | 'salary';
  overtimeEligible: boolean;
  
  // Benefits & Leave
  vacationAccrualRate: number;
  sickLeaveBalance: number;
  benefits: {
    healthInsurance: boolean;
    dentalInsurance: boolean;
    retirement401k: boolean;
    lifeInsurance: boolean;
  };
  
  // Compliance & Legal
  i9Status: 'pending' | 'verified' | 'expired';
  i9ExpirationDate?: string;
  backgroundCheckStatus: 'pending' | 'cleared' | 'failed';
  backgroundCheckDate?: string;
  drugTestStatus: 'pending' | 'passed' | 'failed';
  drugTestDate?: string;
  
  // Tax Information
  taxWithholdings: {
    federalExemptions: number;
    stateExemptions: number;
    additionalWithholding: number;
  };
  
  // Direct Deposit
  directDeposit: {
    accountType: string;
    routingNumber: string;
    accountNumber: string; // Should be encrypted
  };
  
  // System Fields
  isActive: boolean;
  pin: string;
  role: string;
  imageUrl?: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

interface Job {
  jobId: string;
  jobTitle: string;
  department: string;
  payRateMin: number;
  payRateMax: number;
  defaultPayRate: number;
  payType: 'hourly' | 'salary';
  description: string;
  requirements: string;
  isActive: boolean;
}

interface EmployeeManagementEnhancedProps {
  enterpriseId?: string;
  storeId?: string;
  user?: any;
}

export const EmployeeManagementEnhanced: React.FC<EmployeeManagementEnhancedProps> = ({
  enterpriseId: propEnterpriseId,
  storeId: propStoreId
}) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [enterprises, setEnterprises] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState<string>(propEnterpriseId || '');
  const [selectedStoreId, setSelectedStoreId] = useState<string>(propStoreId || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState<'list' | 'form' | 'compliance'>('list');
  const [sortField, setSortField] = useState<string>('firstName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  
  // Form state for comprehensive employee data
  const [formData, setFormData] = useState<Partial<Employee>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    additionalStoreIds: [],
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA'
    },
    emergencyContact: {
      name: '',
      relationship: '',
      phone: '',
      email: ''
    },
    jobId: '',
    jobTitle: '',
    department: '',
    hireDate: new Date().toISOString().split('T')[0],
    employmentType: 'full-time',
    status: 'active',
    hourlyRate: 0,
    payType: 'hourly',
    overtimeEligible: true,
    vacationAccrualRate: 0,
    sickLeaveBalance: 0,
    benefits: {
      healthInsurance: false,
      dentalInsurance: false,
      retirement401k: false,
      lifeInsurance: false
    },
    i9Status: 'pending',
    backgroundCheckStatus: 'pending',
    drugTestStatus: 'pending',
    taxWithholdings: {
      federalExemptions: 0,
      stateExemptions: 0,
      additionalWithholding: 0
    },
    directDeposit: {
      accountType: '',
      routingNumber: '',
      accountNumber: ''
    },
    isActive: true,
    pin: '',
    role: 'employee',
    notes: ''
  });
  
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Sync props with local state when parent updates
  useEffect(() => {
    if (propEnterpriseId && propEnterpriseId !== selectedEnterpriseId) {
      setSelectedEnterpriseId(propEnterpriseId);
    }
  }, [propEnterpriseId]);

  useEffect(() => {
    if (propStoreId && propStoreId !== selectedStoreId) {
      setSelectedStoreId(propStoreId);
    }
  }, [propStoreId]);

  useEffect(() => {
    fetchEnterprises();
    fetchStores();
  }, []);

  useEffect(() => {
    if (selectedEnterpriseId) {
      fetchEmployees();
      fetchJobs();
    } else {
      // No enterprise selected - stop loading and clear employees
      setLoading(false);
      setEmployees([]);
    }
  }, [currentPage, searchTerm, statusFilter, selectedEnterpriseId, selectedStoreId]);

  const fetchEnterprises = async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.enterprises());

      if (response.ok) {
        const data = await response.json();
        const enterprisesList = data.data || [];
        setEnterprises(enterprisesList);

        // Auto-select first enterprise if none selected
        if (!selectedEnterpriseId && enterprisesList.length > 0) {
          setSelectedEnterpriseId(enterprisesList[0].enterpriseId);
        }
      }
    } catch (err) {
      console.warn('Could not fetch enterprises:', err);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.stores());

      if (response.ok) {
        const data = await response.json();
        setStores(data.data || []);
      }
    } catch (err) {
      console.warn('Could not fetch stores:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });

      // Don't add enterpriseId here since API_ENDPOINTS.employees() already includes it
      if (selectedStoreId) params.append('storeId', selectedStoreId);
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      // Use enhanced employees microservice API with authenticatedFetch
      const baseUrl = API_ENDPOINTS.employees(selectedEnterpriseId);
      const separator = baseUrl.includes('?') ? '&' : '?';
      console.log('[EmployeeManagement] Fetching employees from:', `${baseUrl}${separator}${params}`);

      const response = await authenticatedFetch(`${baseUrl}${separator}${params}`);
      console.log('[EmployeeManagement] Employees response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[EmployeeManagement] Error response:', errorText);
        throw new Error('Failed to fetch employees');
      }

      const data = await response.json();
      console.log('[EmployeeManagement] Employees data:', data);
      setEmployees(data.employees || data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setError(null);
    } catch (err) {
      console.error('[EmployeeManagement] Error fetching employees:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      // Fetch jobs from enhanced employees API
      const response = await authenticatedFetch(API_ENDPOINTS.jobs(selectedEnterpriseId));

      if (response.ok) {
        const data = await response.json();
        setJobs(data.data || []);
      }
    } catch (err) {
      console.warn('Could not fetch jobs:', err);
      // Set default jobs if API call fails
      setJobs([
        { jobId: '1', jobTitle: 'Server', department: 'Front of House', payRateMin: 15, payRateMax: 25, defaultPayRate: 18, payType: 'hourly', description: '', requirements: '', isActive: true },
        { jobId: '2', jobTitle: 'Cook', department: 'Kitchen', payRateMin: 16, payRateMax: 28, defaultPayRate: 20, payType: 'hourly', description: '', requirements: '', isActive: true },
        { jobId: '3', jobTitle: 'Manager', department: 'Management', payRateMin: 25, payRateMax: 40, defaultPayRate: 32, payType: 'hourly', description: '', requirements: '', isActive: true }
      ]);
    }
  };

  const handleJobChange = (jobId: string) => {
    const selectedJob = jobs.find(job => job.jobId === jobId);
    if (selectedJob) {
      setFormData({
        ...formData,
        jobId: selectedJob.jobId,
        jobTitle: selectedJob.jobTitle,
        department: selectedJob.department,
        hourlyRate: selectedJob.defaultPayRate,
        payType: selectedJob.payType
      });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.firstName?.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName?.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.jobId) {
      errors.jobId = 'Job selection is required';
    }

    if (!formData.hireDate) {
      errors.hireDate = 'Hire date is required';
    }

    if (formData.hourlyRate && formData.hourlyRate <= 0) {
      errors.hourlyRate = 'Hourly rate must be greater than 0';
    }

    // PIN validation
    if (!formData.pin?.trim()) {
      errors.pin = 'PIN is required';
    } else if (!/^\d{5}$/.test(formData.pin)) {
      errors.pin = 'PIN must be exactly 5 digits';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Validate PIN uniqueness within the store
    const pinIsUnique = await validatePinUniqueness(formData.pin!, editingEmployee?.employeeId);
    if (!pinIsUnique) {
      setFormErrors({...formErrors, pin: 'This PIN is already in use by another employee in this store'});
      return;
    }

    setSubmitting(true);
    
    try {
      const employeeData = {
        ...formData,
        enterpriseId: selectedEnterpriseId,
        storeId: selectedStoreId || '17531284480452mew7it0s' // NOR001 store ID as fallback
      };

      const isUpdate = editingEmployee !== null;
      const url = isUpdate
        ? API_ENDPOINTS.employee(editingEmployee.employeeId)
        : API_ENDPOINTS.employees();

      const response = await authenticatedFetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(employeeData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${isUpdate ? 'update' : 'create'} employee`);
      }

      const result = await response.json();
      
      // Show success message with invitation info for new employees
      if (!isUpdate && result.cognitoStatus && result.cognitoStatus.success) {
        alert(`Employee created successfully!\n\nAn invitation email with login instructions has been sent to ${employeeData.email}.\n\nThe employee will need to check their email to complete their account setup.`);
      } else if (!isUpdate) {
        alert('Employee created successfully!');
      }
      
      // Refresh the employee list
      await fetchEmployees();
      
      // Close the form and reset
      setShowAddForm(false);
      setActiveTab('list');
      setEditingEmployee(null);
      resetForm();
      setError(null);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${editingEmployee ? 'update' : 'create'} employee`);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      additionalStoreIds: [],
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA'
      },
      emergencyContact: {
        name: '',
        relationship: '',
        phone: '',
        email: ''
      },
      jobId: '',
      jobTitle: '',
      department: '',
      hireDate: new Date().toISOString().split('T')[0],
      employmentType: 'full-time',
      status: 'active',
      hourlyRate: 0,
      payType: 'hourly',
      overtimeEligible: true,
      vacationAccrualRate: 0,
      sickLeaveBalance: 0,
      benefits: {
        healthInsurance: false,
        dentalInsurance: false,
        retirement401k: false,
        lifeInsurance: false
      },
      i9Status: 'pending',
      backgroundCheckStatus: 'pending',
      drugTestStatus: 'pending',
      taxWithholdings: {
        federalExemptions: 0,
        stateExemptions: 0,
        additionalWithholding: 0
      },
      directDeposit: {
        accountType: '',
        routingNumber: '',
        accountNumber: ''
      },
      isActive: true,
      pin: '',
      role: 'employee',
      notes: ''
    });
    setFormErrors({});
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active': return 'status-badge status-active';
      case 'inactive': return 'status-badge status-inactive';
      case 'terminated': return 'status-badge status-terminated';
      case 'on_leave': return 'status-badge status-on-leave';
      default: return 'status-badge';
    }
  };

  const getComplianceStatus = (employee: Employee) => {
    const i9Status = employee.i9Status || 'pending';
    const bgStatus = employee.backgroundCheckStatus || 'pending';
    const drugStatus = employee.drugTestStatus || 'pending';
    
    if (i9Status === 'verified' && bgStatus === 'cleared' && drugStatus === 'passed') {
      return { status: 'compliant', label: 'Compliant', class: 'status-active' };
    } else if (i9Status === 'expired' || bgStatus === 'failed' || drugStatus === 'failed') {
      return { status: 'non-compliant', label: 'Non-Compliant', class: 'status-terminated' };
    } else {
      return { status: 'pending', label: 'Pending', class: 'status-inactive' };
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedEmployees = () => {
    return [...employees].sort((a, b) => {
      let aValue = a[sortField as keyof Employee] || '';
      let bValue = b[sortField as keyof Employee] || '';
      
      // Handle nested objects
      if (sortField === 'fullName') {
        aValue = `${a.firstName} ${a.lastName}`;
        bValue = `${b.firstName} ${b.lastName}`;
      }
      
      // Convert to string for comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  };

  const handleEditEmployee = (employee: Employee) => {
    setEditingEmployee(employee);
    setFormData({
      ...employee,
      address: employee.address || {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA'
      },
      emergencyContact: employee.emergencyContact || {
        name: '',
        relationship: '',
        phone: '',
        email: ''
      },
      benefits: employee.benefits || {
        healthInsurance: false,
        dentalInsurance: false,
        retirement401k: false,
        lifeInsurance: false
      },
      taxWithholdings: employee.taxWithholdings || {
        federalExemptions: 0,
        stateExemptions: 0,
        additionalWithholding: 0
      },
      directDeposit: employee.directDeposit || {
        accountType: '',
        routingNumber: '',
        accountNumber: ''
      }
    });
    setShowAddForm(true);
    setActiveTab('form');
  };

  const validatePinUniqueness = async (pin: string, currentEmployeeId?: string): Promise<boolean> => {
    if (!pin || pin.length !== 5) return false;
    
    // Check against existing employees in the current store
    const storeEmployees = employees.filter(emp => 
      emp.storeId === (selectedStoreId || formData.storeId) && 
      emp.employeeId !== currentEmployeeId
    );
    
    return !storeEmployees.some(emp => emp.pin === pin);
  };

  if (loading) return <div className="loading">Loading employees...</div>;

  // Show message if no enterprise is selected (parent handles selection)
  if (!selectedEnterpriseId) {
    return (
      <div className="employee-management">
        <div className="no-enterprise-selected">
          <p>Please select an enterprise above to view employees.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-management">
      <div className="header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end' }}>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => {
              setShowAddForm(true);
              setActiveTab('form');
            }}
          >
            Add Employee
          </button>
        </div>
      </div>

      {error && <div className="error">Error: {error}</div>}

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          Employee List
        </button>
        <button 
          className={`tab ${activeTab === 'form' ? 'active' : ''}`}
          onClick={() => setActiveTab('form')}
        >
          {showAddForm ? 'Add Employee' : 'Employee Form'}
        </button>
        <button 
          className={`tab ${activeTab === 'compliance' ? 'active' : ''}`}
          onClick={() => setActiveTab('compliance')}
        >
          Compliance Tracking
        </button>
      </div>

      {activeTab === 'list' && (
        <div className="employee-list-section">
          <div className="filters">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="status-filter">Status:</label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="on_leave">On Leave</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
          </div>

          <div className="employee-table sortable-grid">
            <div className="table-header">
              <div 
                className={`header-cell sortable ${sortField === 'fullName' ? 'sorted' : ''}`}
                onClick={() => handleSort('fullName')}
              >
                Employee
                {sortField === 'fullName' && (
                  <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                )}
              </div>
              <div 
                className={`header-cell sortable ${sortField === 'jobTitle' ? 'sorted' : ''}`}
                onClick={() => handleSort('jobTitle')}
              >
                Job & Department
                {sortField === 'jobTitle' && (
                  <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                )}
              </div>
              <div 
                className={`header-cell sortable ${sortField === 'hourlyRate' ? 'sorted' : ''}`}
                onClick={() => handleSort('hourlyRate')}
              >
                Pay Rate
                {sortField === 'hourlyRate' && (
                  <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                )}
              </div>
              <div className="header-cell">Compliance</div>
              <div 
                className={`header-cell sortable ${sortField === 'status' ? 'sorted' : ''}`}
                onClick={() => handleSort('status')}
              >
                Status
                {sortField === 'status' && (
                  <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                )}
              </div>
              <div className="header-cell">Actions</div>
            </div>
            
            {getSortedEmployees().map((employee) => {
              const compliance = getComplianceStatus(employee);
              return (
                <div key={employee.employeeId} className="table-row">
                  <div className="cell employee-info">
                    <div className="employee-info-content">
                      <div className="employee-photo">
                        {employee.imageUrl ? (
                          <img 
                            src={employee.imageUrl} 
                            alt={`${employee.firstName} ${employee.lastName}`}
                            className="employee-profile-photo"
                          />
                        ) : (
                          <div className="employee-photo-placeholder">
                            <span>{employee.firstName?.charAt(0)?.toUpperCase()}{employee.lastName?.charAt(0)?.toUpperCase()}</span>
                          </div>
                        )}
                      </div>
                      <div className="employee-text">
                        <div className="employee-name">
                          {employee.firstName} {employee.lastName}
                        </div>
                        <div className="employee-details">
                          {employee.employeeNumber || employee.employeeId} • {employee.email}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="cell">
                    <div className="job-title">{employee.jobTitle}</div>
                    <div className="department">{employee.department}</div>
                  </div>
                  
                  <div className="cell">
                    <div>{formatCurrency(employee.hourlyRate || 0)}/{employee.payType || 'hourly'}</div>
                    <div className="employment-type">{employee.employmentType || 'full-time'}</div>
                  </div>
                  
                  <div className="cell">
                    <span className={`status-badge ${compliance.class}`}>
                      {compliance.label}
                    </span>
                  </div>
                  
                  <div className="cell">
                    <span className={getStatusBadgeClass(employee.status)}>
                      {employee.status?.replace('_', ' ') || 'Unknown'}
                    </span>
                  </div>
                  
                  <div className="cell actions">
                    <button
                      className="btn btn-small btn-primary"
                      onClick={() => handleEditEmployee(employee)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-small btn-secondary"
                      onClick={() => setSelectedEmployee(employee)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-small"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                className="btn btn-small"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'form' && (
        <div className="employee-form-section">
          <h3>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</h3>
          
          <form onSubmit={handleFormSubmit} className="employee-form">
            {/* Personal Information Section */}
            <div className="form-section">
              <h4>Personal Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    value={formData.firstName || ''}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className={formErrors.firstName ? 'error' : ''}
                    required
                  />
                  {formErrors.firstName && <span className="error-text">{formErrors.firstName}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    value={formData.lastName || ''}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className={formErrors.lastName ? 'error' : ''}
                    required
                  />
                  {formErrors.lastName && <span className="error-text">{formErrors.lastName}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className={formErrors.email ? 'error' : ''}
                    required
                    placeholder="employee@company.com"
                  />
                  <div className="field-help-text">
                    Must be a valid email address - required for employee login and system access.
                  </div>
                  {formErrors.email && <span className="error-text">{formErrors.email}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    value={formData.dateOfBirth || ''}
                    onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="pin">POS PIN (5 digits) *</label>
                  <input
                    type="text"
                    id="pin"
                    maxLength={5}
                    pattern="[0-9]{5}"
                    value={formData.pin || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                      if (value.length <= 5) {
                        setFormData({...formData, pin: value});
                      }
                    }}
                    placeholder="12345"
                    className={formErrors.pin ? 'error' : ''}
                    required
                  />
                  {formErrors.pin && <span className="error-text">{formErrors.pin}</span>}
                </div>
              </div>

              {/* Profile Photo Section */}
              {editingEmployee && (
                <div className="form-row">
                  <div className="form-group full-width">
                    <EmployeeImageUpload
                      employeeId={editingEmployee.employeeId}
                      employeeName={`${formData.firstName} ${formData.lastName}`}
                      currentImageUrl={formData.imageUrl}
                      onImageUpdated={(imageUrl) => {
                        setFormData({...formData, imageUrl});
                        // Update the employee in the employees list immediately
                        setEmployees(prev => prev.map(emp => 
                          emp.employeeId === editingEmployee.employeeId 
                            ? { ...emp, imageUrl } 
                            : emp
                        ));
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Store Assignment Section */}
            <div className="form-section">
              <h4>Store Assignment</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="employeeEnterpriseId">Enterprise *</label>
                  <select
                    id="employeeEnterpriseId"
                    value={formData.enterpriseId || selectedEnterpriseId}
                    onChange={(e) => {
                      setFormData({...formData, enterpriseId: e.target.value, storeId: ''}); // Clear store when enterprise changes
                    }}
                    className={formErrors.enterpriseId ? 'error' : ''}
                    required
                  >
                    <option value="">Select Enterprise...</option>
                    {enterprises.map((enterprise) => (
                      <option key={enterprise.enterpriseId} value={enterprise.enterpriseId}>
                        {enterprise.name || enterprise.enterpriseName || enterprise.enterpriseId}
                      </option>
                    ))}
                  </select>
                  <div className="field-help-text">
                    The enterprise this employee belongs to. This determines which data they can access.
                  </div>
                  {formErrors.enterpriseId && <span className="error-text">{formErrors.enterpriseId}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="employeeStoreId">Home Store *</label>
                  <select
                    id="employeeStoreId"
                    value={formData.storeId || ''}
                    onChange={(e) => setFormData({...formData, storeId: e.target.value})}
                    className={formErrors.storeId ? 'error' : ''}
                    required
                    disabled={!formData.enterpriseId && !selectedEnterpriseId}
                  >
                    <option value="">Select Home Store...</option>
                    {stores
                      .filter(store => store.enterpriseId === (formData.enterpriseId || selectedEnterpriseId))
                      .map((store) => (
                        <option key={store.storeId} value={store.storeId}>
                          {store.storeName || store.storeCode || store.storeId}
                        </option>
                      ))}
                  </select>
                  <div className="field-help-text">
                    The employee's primary/home store. This is where they normally work and where their schedule is managed.
                  </div>
                  {formErrors.storeId && <span className="error-text">{formErrors.storeId}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="additionalStores">Additional Stores (Optional)</label>
                  <div className="multi-select-container">
                    {stores
                      .filter(store => 
                        store.enterpriseId === (formData.enterpriseId || selectedEnterpriseId) &&
                        store.storeId !== formData.storeId // Don't show home store in additional stores
                      )
                      .map((store) => (
                        <label key={store.storeId} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={(formData.additionalStoreIds || []).includes(store.storeId)}
                            onChange={(e) => {
                              const currentAdditional = formData.additionalStoreIds || [];
                              const newAdditional = e.target.checked
                                ? [...currentAdditional, store.storeId]
                                : currentAdditional.filter(id => id !== store.storeId);
                              setFormData({...formData, additionalStoreIds: newAdditional});
                            }}
                          />
                          <span className="checkbox-text">
                            {store.storeName || store.storeCode || store.storeId}
                          </span>
                        </label>
                      ))}
                  </div>
                  <div className="field-help-text">
                    Additional stores where this employee can work and authenticate. Employee can use POS systems at any of these locations.
                  </div>
                </div>
              </div>
            </div>

            {/* Employment Information Section */}
            <div className="form-section">
              <h4>Employment Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="jobId">Job Title *</label>
                  <select
                    id="jobId"
                    value={formData.jobId || ''}
                    onChange={(e) => handleJobChange(e.target.value)}
                    className={formErrors.jobId ? 'error' : ''}
                    required
                  >
                    <option value="">Select a job...</option>
                    {jobs.filter(job => job.isActive).map(job => (
                      <option key={job.jobId} value={job.jobId}>
                        {job.jobTitle} - {job.department} (${job.payRateMin}-${job.payRateMax}/hr)
                      </option>
                    ))}
                  </select>
                  {formErrors.jobId && <span className="error-text">{formErrors.jobId}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="employmentType">Employment Type</label>
                  <select
                    id="employmentType"
                    value={formData.employmentType || 'full-time'}
                    onChange={(e) => setFormData({...formData, employmentType: e.target.value as any})}
                  >
                    <option value="full-time">Full-Time</option>
                    <option value="part-time">Part-Time</option>
                    <option value="contractor">Contractor</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="hireDate">Hire Date *</label>
                  <input
                    type="date"
                    id="hireDate"
                    value={formData.hireDate || ''}
                    onChange={(e) => setFormData({...formData, hireDate: e.target.value})}
                    className={formErrors.hireDate ? 'error' : ''}
                    required
                  />
                  {formErrors.hireDate && <span className="error-text">{formErrors.hireDate}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    value={formData.status || 'active'}
                    onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="on_leave">On Leave</option>
                    <option value="terminated">Terminated</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Compensation Section */}
            <div className="form-section">
              <h4>Compensation</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="hourlyRate">Hourly Rate ($)</label>
                  <input
                    type="number"
                    id="hourlyRate"
                    step="0.01"
                    min="0"
                    value={formData.hourlyRate || ''}
                    onChange={(e) => setFormData({...formData, hourlyRate: parseFloat(e.target.value) || 0})}
                    className={formErrors.hourlyRate ? 'error' : ''}
                  />
                  {formErrors.hourlyRate && <span className="error-text">{formErrors.hourlyRate}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="payType">Pay Type</label>
                  <select
                    id="payType"
                    value={formData.payType || 'hourly'}
                    onChange={(e) => setFormData({...formData, payType: e.target.value as any})}
                  >
                    <option value="hourly">Hourly</option>
                    <option value="salary">Salary</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.overtimeEligible || false}
                      onChange={(e) => setFormData({...formData, overtimeEligible: e.target.checked})}
                    />
                    Overtime Eligible
                  </label>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setActiveTab('list');
                  setEditingEmployee(null);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? (editingEmployee ? 'Updating...' : 'Creating...') : (editingEmployee ? 'Update Employee' : 'Create Employee')}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'compliance' && (
        <div className="compliance-section">
          <h3>Compliance Tracking</h3>
          <div className="compliance-grid">
            {employees.map((employee) => {
              const compliance = getComplianceStatus(employee);
              return (
                <div key={employee.employeeId} className="compliance-card">
                  <div className="employee-header">
                    <h4>{employee.firstName} {employee.lastName}</h4>
                    <span className={`status-badge ${compliance.class}`}>
                      {compliance.label}
                    </span>
                  </div>
                  
                  <div className="compliance-details">
                    <div className="compliance-item">
                      <label>I-9 Status:</label>
                      <span className={`status ${employee.i9Status || 'pending'}`}>
                        {(employee.i9Status || 'pending').replace('_', ' ')}
                      </span>
                    </div>
                    
                    <div className="compliance-item">
                      <label>Background Check:</label>
                      <span className={`status ${employee.backgroundCheckStatus || 'pending'}`}>
                        {(employee.backgroundCheckStatus || 'pending').replace('_', ' ')}
                      </span>
                    </div>
                    
                    <div className="compliance-item">
                      <label>Drug Test:</label>
                      <span className={`status ${employee.drugTestStatus || 'pending'}`}>
                        {(employee.drugTestStatus || 'pending').replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedEmployee && (
        <div className="modal-overlay">
          <div className="modal employee-details-modal">
            <div className="modal-header">
              <h3>Employee Details - {selectedEmployee.firstName} {selectedEmployee.lastName}</h3>
              <button
                className="btn btn-small btn-secondary"
                onClick={() => setSelectedEmployee(null)}
              >
                Close
              </button>
            </div>
            
            <div className="modal-content">
              <div className="employee-details-grid">
                <div className="detail-section">
                  <h4>Personal Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Name:</label>
                      <span>{selectedEmployee.firstName} {selectedEmployee.lastName}</span>
                    </div>
                    <div className="detail-item">
                      <label>Employee #:</label>
                      <span>{selectedEmployee.employeeNumber || selectedEmployee.employeeId}</span>
                    </div>
                    <div className="detail-item">
                      <label>Email:</label>
                      <span>{selectedEmployee.email}</span>
                    </div>
                    <div className="detail-item">
                      <label>Phone:</label>
                      <span>{selectedEmployee.phone || 'Not provided'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Date of Birth:</label>
                      <span>{selectedEmployee.dateOfBirth || 'Not provided'}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Employment Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Job Title:</label>
                      <span>{selectedEmployee.jobTitle}</span>
                    </div>
                    <div className="detail-item">
                      <label>Department:</label>
                      <span>{selectedEmployee.department}</span>
                    </div>
                    <div className="detail-item">
                      <label>Hire Date:</label>
                      <span>{new Date(selectedEmployee.hireDate).toLocaleDateString()}</span>
                    </div>
                    <div className="detail-item">
                      <label>Employment Type:</label>
                      <span>{selectedEmployee.employmentType || 'full-time'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Status:</label>
                      <span className={getStatusBadgeClass(selectedEmployee.status)}>
                        {selectedEmployee.status?.replace('_', ' ') || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Compensation</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Pay Rate:</label>
                      <span>{formatCurrency(selectedEmployee.hourlyRate || 0)}/{selectedEmployee.payType || 'hourly'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Overtime Eligible:</label>
                      <span>{selectedEmployee.overtimeEligible ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Compliance Status</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>I-9 Status:</label>
                      <span className={`status ${selectedEmployee.i9Status || 'pending'}`}>
                        {(selectedEmployee.i9Status || 'pending').replace('_', ' ')}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Background Check:</label>
                      <span className={`status ${selectedEmployee.backgroundCheckStatus || 'pending'}`}>
                        {(selectedEmployee.backgroundCheckStatus || 'pending').replace('_', ' ')}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Drug Test:</label>
                      <span className={`status ${selectedEmployee.drugTestStatus || 'pending'}`}>
                        {(selectedEmployee.drugTestStatus || 'pending').replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};